// Right-hand tabbed dock (D-40): tab strip, "素材" (one card per source, spec table) and "調整".

import { DEFAULT_EXR_VIEW, type ExrView } from '../core/sequence';
import type { App } from '../app/app';
import type { DockTab } from '../app/store';
import type { Channel, FrameMapping, Levels, SequenceInfo, Source, SourceId, VideoSpec } from '../core/types';
import { colorTagHint, compareWarnings, fmtFps, sourceWarnings, detectMapping, type SpecWarning } from '../core/spec-check';
import { normalizeMatrix, codeToNorm, normToCode, LEVELS_IDENTITY } from '../core/color';
import { t } from '../i18n';
import { $, $$, esc, fmtSigned, segmented, setText, toast } from './dom';
import { openSourceMenu } from './source-menu';
import { refreshTemporalSlots } from './temporal-panel';
import { makePresetFile } from '../core/presets';
import { slotLabel } from '../core/types';
import { AI_ANALYSIS_ENABLED } from '../ai/enabled';

// Analysis (F-10.10) is taken off the interface while it is paused (owner, 2026-10-03).
const TABS: DockTab[] = AI_ANALYSIS_ENABLED ? ['info', 'check', 'adjust', 'analysis'] : ['info', 'check', 'adjust'];
const CHANNELS: Channel[] = ['rgb', 'r', 'g', 'b', 'luma'];
const LEVEL_TARGETS = ['rgb', 'r', 'g', 'b'] as const;

let updPresets: (active: string | null) => void;
let presetIds: string[] = [];
let updLevelTarget: ReturnType<typeof segmented<(typeof LEVEL_TARGETS)[number]>>;
let updChannel: ReturnType<typeof segmented<Channel>>;

export function initDock(app: App): void {
  $('#dockTabs').addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-tab]');
    if (b) app.store.ui((u) => (u.dockTab = b.dataset.tab as DockTab));
  });
  initAdjust(app);
  initInfo(app);
}

export function updateDock(app: App): void {
  const ui = app.state.ui;
  if (!TABS.includes(ui.dockTab)) ui.dockTab = 'info';
  $('#dock').classList.toggle('show', ui.dockOpen);
  const tabsHtml = TABS.map(
    (tb) =>
      `<button data-tab="${tb}" class="${tb === ui.dockTab ? 'on' : ''}">${esc(t('dock.' + tb))}</button>`,
  ).join('');
  const tabs = $('#dockTabs');
  if (tabs.innerHTML !== tabsHtml) tabs.innerHTML = tabsHtml;
  $$('.tab-body').forEach((el) => el.classList.toggle('show', el.dataset.tab === ui.dockTab));
  if (!ui.dockOpen) return;
  if (ui.dockTab === 'adjust') updateAdjust(app);
  if (ui.dockTab === 'info') updateInfo(app);
}

// ------------------------------------------------------------------------------------------------ adjust (F-16)

function levelsBitDepth(app: App): number {
  const id = app.project.viewAdjustment.linked ? 'A' : app.project.layout.selectedB;
  return app.sources.source(id)?.spec.bitDepth ?? app.sources.source('A')?.spec.bitDepth ?? 8;
}

function currentLevels(app: App): Levels {
  const adj = app.project.viewAdjustment;
  const tgt = app.state.ui.levelTarget;
  if (tgt === 'rgb') return adj.levels;
  return adj.perChannel?.[tgt] ?? { ...LEVELS_IDENTITY };
}

function initAdjust(app: App): void {
  const upd = (mut: (p: import('../core/types').Project) => void, key: string) => app.store.project(mut, { mergeKey: key });
  const enabled = $<HTMLInputElement>('#adjEnabled');
  enabled.addEventListener('change', () => upd((p) => (p.viewAdjustment.enabled = enabled.checked), 'adj-en'));
  const linked = $<HTMLInputElement>('#adjLinked');
  linked.addEventListener('change', () => upd((p) => (p.viewAdjustment.linked = linked.checked), 'adj-link'));
  const exp = $<HTMLInputElement>('#adjExposure');
  exp.addEventListener('input', () =>
    upd((p) => {
      p.viewAdjustment.exposureStops = Number(exp.value);
      p.viewAdjustment.enabled = true;
      p.viewAdjustment.presetId = undefined;
    }, 'adj-exp'),
  );
  const gam = $<HTMLInputElement>('#adjGamma');
  gam.addEventListener('input', () =>
    upd((p) => {
      p.viewAdjustment.gamma = Number(gam.value);
      p.viewAdjustment.enabled = true;
      p.viewAdjustment.presetId = undefined;
    }, 'adj-gam'),
  );
  const lv = (id: string, key: keyof Levels, isCode: boolean) => {
    const el = $<HTMLInputElement>(id);
    el.addEventListener('input', () => {
      const bd = levelsBitDepth(app);
      const v = isCode ? codeToNorm(Number(el.value), bd) : Number(el.value);
      upd((p) => {
        const adj = p.viewAdjustment;
        const tgt = app.state.ui.levelTarget;
        let L: Levels;
        if (tgt === 'rgb') L = adj.levels;
        else {
          adj.perChannel ??= { r: { ...LEVELS_IDENTITY }, g: { ...LEVELS_IDENTITY }, b: { ...LEVELS_IDENTITY } };
          L = adj.perChannel[tgt];
        }
        L[key] = v;
        if (key === 'inBlack' && L.inBlack >= L.inWhite) L.inWhite = Math.min(1, L.inBlack + 1 / 1023);
        if (key === 'inWhite' && L.inWhite <= L.inBlack) L.inBlack = Math.max(0, L.inWhite - 1 / 1023);
        adj.enabled = true;
        adj.presetId = undefined;
      }, 'adj-' + id);
    });
  };
  lv('#lvInBlack', 'inBlack', true);
  lv('#lvInWhite', 'inWhite', true);
  lv('#lvMid', 'mid', false);
  lv('#lvOutBlack', 'outBlack', true);
  lv('#lvOutWhite', 'outWhite', true);
  $('#adjReset').addEventListener('click', () =>
    app.store.project((p) => {
      p.viewAdjustment = { ...p.viewAdjustment, exposureStops: 0, gamma: 1, levels: { ...LEVELS_IDENTITY }, perChannel: undefined, presetId: undefined };
    }),
  );
  $('#adjSavePreset').addEventListener('click', () => void app.saveCustomPreset());
  updLevelTarget = segmented($('#segLevelTarget'), LEVEL_TARGETS, (c) => t('channel.' + c), (c) => app.store.ui((u) => (u.levelTarget = c)));
  updChannel = segmented($('#segChannel'), CHANNELS, (c) => t('channel.' + c), (c) => app.store.project((p) => (p.layout.channel = c), { undo: false }));
  const tex = $<HTMLInputElement>('#adjTexture');
  tex.addEventListener('change', () => app.store.project((p) => (p.layout.textureView = tex.checked), { undo: false }));
}

function rebuildPresets(app: App): void {
  const list = app.adjustPresets();
  const ids = list.map((p) => p.id + (p.labelKey ? t(p.labelKey) : p.name));
  if (ids.join('|') === presetIds.join('|')) return;
  presetIds = ids;
  updPresets = segmented(
    $('#segPresets'),
    list.map((p) => p.id),
    (id) => {
      const p = list.find((x) => x.id === id)!;
      return p.labelKey ? t(p.labelKey) : (p.name ?? id);
    },
    (id) => app.applyPreset(id),
  );
}

function updateAdjust(app: App): void {
  const adj = app.project.viewAdjustment;
  rebuildPresets(app);
  updPresets(adj.presetId ?? null);
  $<HTMLInputElement>('#adjEnabled').checked = adj.enabled;
  $<HTMLInputElement>('#adjLinked').checked = adj.linked;
  $<HTMLInputElement>('#adjExposure').value = String(adj.exposureStops);
  setText($('#adjExposureOut'), (adj.exposureStops > 0 ? '+' : '') + adj.exposureStops.toFixed(1));
  $<HTMLInputElement>('#adjGamma').value = String(adj.gamma);
  setText($('#adjGammaOut'), adj.gamma.toFixed(2));
  updLevelTarget(app.state.ui.levelTarget);
  const bd = levelsBitDepth(app);
  const max = 2 ** bd - 1;
  const L = currentLevels(app);
  for (const [id, key] of [
    ['#lvInBlack', 'inBlack'],
    ['#lvInWhite', 'inWhite'],
    ['#lvOutBlack', 'outBlack'],
    ['#lvOutWhite', 'outWhite'],
  ] as [string, keyof Levels][]) {
    const el = $<HTMLInputElement>(id);
    el.min = '0';
    el.max = String(max);
    el.step = '1';
    const code = normToCode(L[key], bd);
    if (document.activeElement !== el) el.value = String(code);
    setText($(id + 'Out'), String(code));
  }
  $<HTMLInputElement>('#lvMid').value = String(L.mid);
  setText($('#lvMidOut'), L.mid.toFixed(2));
  updChannel(app.project.layout.channel);
  $<HTMLInputElement>('#adjTexture').checked = app.project.layout.textureView;
}

// ------------------------------------------------------------------------------------------------ info (F-01.3/F-01.4)

function initInfo(app: App): void {
  const host = $('#sourceCards');
  host.addEventListener('change', (e) => {
    const el = e.target as HTMLInputElement | HTMLSelectElement;
    const id = el.closest<HTMLElement>('[data-src]')?.dataset.src as SourceId | undefined;
    if (!id) return;
    const field = el.dataset.field;
    if (field === 'mapping') {
      const v = el.value;
      app.store.project((p) => {
        const s = p.sources.find((x) => x.id === id)!;
        if (v === 'auto') {
          s.frameMappingManual = false;
          const a = p.sources.find((x) => x.id === 'A');
          if (a) s.frameMapping = detectMapping(a.spec, s.spec).mapping;
        } else {
          s.frameMappingManual = true;
          s.frameMapping = v === 'one-to-one' || v === 'time' ? { mode: v } : ({ mode: 'ratio', ratio: Number(v.slice(1)) } as FrameMapping);
        }
      });
      app.sources.rebuildTimeline();
      app.player.seek(app.player.tick);
    }
    if (field === 'seqFps') app.setSequenceFps(id, Number(el.value));
    if (field === 'exrGamut') app.setExrView(id, { gamut: el.value as ExrView['gamut'] });
    if (field === 'exrTransfer') app.setExrView(id, { transfer: el.value as ExrView['transfer'] });
    if (field === 'exrExposure') app.setExrView(id, { exposure: Math.max(-8, Math.min(8, Number(el.value) || 0)) });
    if (field === 'matrix' || field === 'range') {
      app.store.project((p) => {
        const s = p.sources.find((x) => x.id === id)!;
        s.colorOverride = { ...(s.colorOverride ?? {}) };
        if (field === 'matrix') s.colorOverride.matrixCoefficients = el.value === 'auto' ? undefined : (el.value as 'bt709' | 'bt601');
        else s.colorOverride.range = el.value === 'auto' ? undefined : (el.value as 'limited' | 'full');
      });
      app.view.request();
    }
  });
  host.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const id = target.closest<HTMLElement>('[data-src]')?.dataset.src as SourceId | undefined;
    if (!id) return;
    // The card's "⋯": the same menu as on the pane (F-04.8).
    const more = target.closest<HTMLElement>('[data-more]');
    if (more) {
      const r = more.getBoundingClientRect();
      openSourceMenu(app, id, r.left, r.bottom + 4);
      return;
    }
    const act = target.closest<HTMLElement>('[data-act]')?.dataset.act;
    if (act === 'offDown') app.setOffset(id, -1);
    if (act === 'offUp') app.setOffset(id, 1);
    if (act === 'colorLikeA') {
      // Same conversion as A (matrix and range), as if chosen in the advanced colour-tag settings.
      const s = colorTagHint(app.sources.color('A'), app.sources.color(id))?.suggest;
      if (!s) return;
      app.store.project((p) => {
        const src = p.sources.find((x) => x.id === id)!;
        src.colorOverride = { ...(src.colorOverride ?? {}), matrixCoefficients: s.matrix, range: s.range };
      });
      app.view.request();
    }
  });
}

let infoKey = '';

const SEQ_FPS = [23.976, 24, 25, 29.97, 30, 48, 50, 59.94, 60];

/** Frame rate and EXR view controls of an image-sequence source (F-01.12). */
function sequenceControls(seq: SequenceInfo, fps: number): string {
  const opt = (v: string, l: string, cur: string) => `<option value="${v}"${v === cur ? ' selected' : ''}>${esc(l)}</option>`;
  const fpsList = SEQ_FPS.includes(Math.round(fps * 1000) / 1000) ? SEQ_FPS : [...SEQ_FPS, fps].sort((a, b) => a - b);
  let html =
    `<div class="row2"><span class="lbl">${esc(t('seq.fps'))}</span><select data-field="seqFps">` +
    fpsList.map((v) => opt(String(v), `${v} fps`, String(Math.round(fps * 1000) / 1000))).join('') +
    `</select><span class="muted small">${esc(t('seq.frames', { pattern: seq.pattern, first: seq.first, last: seq.first + seq.count - 1 }))}</span></div>`;
  if (seq.kind === 'exr') {
    const v = { ...DEFAULT_EXR_VIEW, ...seq.exrView };
    html +=
      `<div class="row2"><span class="lbl">${esc(t('seq.exrGamut'))}</span><select data-field="exrGamut">` +
      (['rec709', 'acescg', 'aces2065'] as const).map((g) => opt(g, t('seq.gamut.' + g), v.gamut)).join('') +
      `</select><select data-field="exrTransfer">` +
      (['srgb', 'rec709', 'gamma24', 'linear'] as const).map((g) => opt(g, t('seq.transfer.' + g), v.transfer)).join('') +
      `</select></div><div class="row2"><span class="lbl">${esc(t('seq.exposure'))}</span><input type="number" data-field="exrExposure" min="-8" max="8" step="0.5" value="${v.exposure}" style="width:70px" /><span class="muted small">${esc(t('seq.exrNote'))}</span></div>`;
  }
  return html;
}

/** Warnings shown on a card; the other spec differences are folded under "Spec differences" (D-48). */
/** Colour differences to A: shown as the colour-tag reminder instead (C-04). */
const COLOR_KEYS = new Set(['spec.warn.matrix', 'spec.warn.colorTags', 'spec.warn.range']);

const IMPORTANT_WARNINGS = new Set([
  'spec.warn.undecodable',
  'spec.warn.vfr',
  'spec.warn.interlaced',
  'spec.warn.rotation',
  'spec.warn.hdr',
  'spec.warn.frameCount',
  'spec.warn.fps',
  'spec.warn.timeMapping',
]);

function warningHtml(w: SpecWarning, prefix: string): string {
  return `<div class="warn-item ${w.level === 'warn' ? '' : w.level}">${esc(prefix + t(w.key, w.params))}</div>`;
}

/** Codec family for the one-line summary ("avc1.640028" → "H.264"). */
function codecName(codec: string): string {
  const c = codec.toLowerCase();
  if (c.startsWith('avc')) return 'H.264';
  if (c.startsWith('hev') || c.startsWith('hvc')) return 'HEVC';
  if (c.startsWith('av01')) return 'AV1';
  if (c.startsWith('vp09') || c === 'vp9') return 'VP9';
  if (c.startsWith('vp8')) return 'VP8';
  return codec.toUpperCase();
}

/** Mapping and colour (or sequence) settings, in the card's collapsed "Advanced" part. */
function advancedHtml(x: { id: SourceId; s: VideoSpec; src: Source }): string {
  const m = x.src.frameMapping;
  const mv = x.src.frameMappingManual ? (m.mode === 'ratio' ? `r${m.ratio}` : m.mode) : 'auto';
  const co = x.src.colorOverride ?? {};
  const opt = (v: string, l: string, cur: string) => `<option value="${v}"${v === cur ? ' selected' : ''}>${esc(l)}</option>`;
  let html = '';
  if (x.id !== 'A')
    html +=
      `<div class="row2"><span class="lbl">${esc(t('info.mapping'))}</span><select data-field="mapping">` +
      [
        ['auto', t('info.auto')],
        ['one-to-one', t('mapping.one-to-one')],
        ['r2', t('mapping.ratio', { n: 2 })],
        ['r3', t('mapping.ratio', { n: 3 })],
        ['r4', t('mapping.ratio', { n: 4 })],
        ['time', t('mapping.time')],
      ]
        .map(([v, l]) => opt(v, l, mv))
        .join('') +
      '</select></div>';
  if (x.src.sequence) html += sequenceControls(x.src.sequence, x.s.frameRate);
  else
    html +=
      `<div class="row2"><span class="lbl">${esc(t('info.colorOverride'))}</span><select data-field="matrix">` +
      [
        ['auto', t('info.auto')],
        ['bt709', 'BT.709'],
        ['bt601', 'BT.601'],
      ]
        .map(([v, l]) => opt(v, l, co.matrixCoefficients ?? 'auto'))
        .join('') +
      `</select><select data-field="range">` +
      [
        ['auto', t('info.auto')],
        ['limited', 'Limited'],
        ['full', 'Full'],
      ]
        .map(([v, l]) => opt(v, l, co.range ?? 'auto'))
        .join('') +
      '</select></div>';
  return html;
}

/**
 * The 素材 tab (F-01.3, F-01.4, D-48): one card per source — file, a one-line spec summary, the warnings that may
 * need action (other differences folded), and for a version its time offset, frame check and advanced settings.
 * The full spec comparison table stays below, collapsed.
 */
function updateInfo(app: App): void {
  const ids = app.sources.loadedIds();
  const key = JSON.stringify([
    ids,
    app.project.sources.map((s) => [s.id, s.file.name, s.spatialTransform.appliedFromSuggestion, s.frameMapping, s.frameMappingManual, s.colorOverride, s.spec, s.sequence, s.frameOffset]),
    t('info.specTitle'),
    app.state.settings.frameNumberBase,
  ]);
  if (key === infoKey) return;
  infoKey = key;
  const specs = ids.map((id) => ({ id, s: app.sources.source(id)!.spec, src: app.sources.source(id)! }));
  const cards = $('#sourceCards');
  if (!specs.length) {
    cards.innerHTML = `<p class="muted">${esc(t('info.none'))}</p>`;
    $('#specTable').innerHTML = '';
    return;
  }
  const rows: [string, (x: (typeof specs)[number]) => string][] = [
    ['info.size', (x) => `${x.s.width}×${x.s.height}${x.s.pixelAspectRatio !== 1 ? ` (PAR ${x.s.pixelAspectRatio.toFixed(3)})` : ''}`],
    ['info.codec', (x) => x.s.codec],
    ['info.container', (x) => x.s.container],
    ['info.fps', (x) => fmtFps(x.s.frameRate) + (x.s.isVariableFrameRate ? ' VFR' : '')],
    ['info.frames', (x) => String(x.s.frameCount)],
    ['info.duration', (x) => x.s.durationSec.toFixed(3) + ' s'],
    ['info.bitDepth', (x) => `${x.s.bitDepth}-bit`],
    ['info.chroma', (x) => x.s.chromaSubsampling],
    ['info.color', (x) => [x.s.colorPrimaries, x.s.transferCharacteristics, normalizeMatrix(x.s.matrixCoefficients) ?? x.s.matrixCoefficients].map((v) => v ?? '—').join(' / ')],
    ['info.range', (x) => x.s.range ?? '—'],
    ['info.tc', (x) => x.s.startTimecode?.text ?? t('info.noTc')],
    ['info.audio', (x) => (x.s.audio ? `${x.s.audio.codec} ${x.s.audio.sampleRate} Hz ${x.s.audio.channels}ch ${x.s.audio.durationSec.toFixed(2)} s` : t('info.noAudio'))],
    ['info.bitrate', (x) => (x.s.bitrateKbps ? `${(x.s.bitrateKbps / 1000).toFixed(1)} Mbps` : '—')],
    ['info.gop', (x) => (x.s.maxGopLength ? String(x.s.maxGopLength) : '—')],
    ['info.mapping', (x) => (x.id === 'A' ? '—' : mappingLabel(x.src.frameMapping) + (x.src.frameMappingManual ? '' : ` (${t('info.auto')})`))],
    ['info.decodePath', (x) => decodePath(app, x.id)],
  ];
  // File names are on the cards; the table compares values, and a version's value that differs from A is marked.
  $('#specTable').innerHTML =
    `<table class="data-table spec-cmp"><tr><th></th>${specs.map((x) => `<th>${slotLabel(x.id)}</th>`).join('')}</tr>` +
    rows
      .map(([k, f]) => {
        const vA = f(specs[0]);
        return `<tr><td>${esc(t(k))}</td>${specs.map((x, i) => `<td${i > 0 && specs[0].id === 'A' && f(x) !== vA && k !== 'info.mapping' && k !== 'info.decodePath' ? ' class="differs"' : ''}>${esc(f(x))}</td>`).join('')}</tr>`;
      })
      .join('') +
    '</table>';

  const a = app.sources.source('A');
  // Parts the user opened (advanced settings, spec differences) stay open when the cards are rebuilt.
  const open = new Set([...cards.querySelectorAll<HTMLDetailsElement>('details[data-open-key]')].filter((d) => d.open).map((d) => d.dataset.openKey));
  const det = (k: string, summary: string, body: string) =>
    `<details data-open-key="${k}"${open.has(k) ? ' open' : ''}><summary>${esc(summary)}</summary>${body}</details>`;
  cards.innerHTML = specs
    .map((x) => {
      // Resolution differences are not repeated here: the alignment reminder on the picture covers them (F-03.10).
      const all = [...sourceWarnings(x.s), ...(a && x.id !== 'A' ? compareWarnings(a.spec, x.s, x.src.frameMapping) : [])].filter(
        (w) => w.key !== 'spec.warn.resolution' && !(x.id !== 'A' && COLOR_KEYS.has(w.key)),
      );
      const hint = a && x.id !== 'A' && app.sources.has('A') && !x.src.sequence && !a.sequence ? colorTagHint(app.sources.color('A'), app.sources.color(x.id)) : null;
      // Two levels (D-48): what may need action is shown; other differences are folded away.
      const shown = all.filter((w) => IMPORTANT_WARNINGS.has(w.key));
      const folded = all.filter((w) => !IMPORTANT_WARNINGS.has(w.key));
      const summary = t('card.summary', {
        w: x.s.width,
        h: x.s.height,
        fps: fmtFps(x.s.frameRate),
        n: x.s.frameCount,
        codec: codecName(x.s.codec) + (x.s.bitDepth > 8 ? ` ${x.s.bitDepth}-bit` : ''),
      });
      let html =
        `<div class="src-card" data-src="${x.id}">` +
        `<div class="card-head"><b class="slot">${slotLabel(x.id)}</b><span class="fname" title="${esc(x.src.file.name)}">${esc(x.src.file.name)}</span>` +
        `<button class="more" data-more="${x.id}" title="${esc(t('srcmenu.tip', { id: x.id }))}">⋯</button></div>` +
        `<div class="card-sum muted small">${esc(summary)}</div>` +
        (shown.length || hint
          ? `<div class="card-warns">${shown.map((w) => warningHtml(w, '')).join('')}` +
            (hint ? `<div class="warn-item color-hint"><span>${esc(t(hint.key, hint.params))}</span><button data-act="colorLikeA">${esc(t('color.hint.apply', { a: hint.params.a }))}</button></div>` : '') +
            `</div>`
          : '') +
        (folded.length ? det('diff:' + x.id, t('card.diffs', { n: folded.length }), `<ul class="card-diffs">${folded.map((w) => `<li>${esc(t(w.key, w.params))}</li>`).join('')}</ul>`) : '');
      if (x.id !== 'A') {
        const oneToOne = x.src.frameMapping.mode === 'one-to-one';
        html +=
          `<div class="row2"><span class="lbl">${esc(t('card.offset'))}</span><button data-act="offDown" title="${esc(t('help.k.offDown'))}">−</button>` +
          `<span class="offset-val">${esc(t('card.offsetValue', { n: fmtSigned(x.src.frameOffset) }))}</span><button data-act="offUp" title="${esc(t('help.k.offUp'))}">+</button>` +
          `<button class="card-check" data-act="analyse" data-src="${x.id}" title="${esc(t(oneToOne ? 'temporal.hint' : 'temporal.onlyOneToOne'))}"${oneToOne ? '' : ' disabled'}>${esc(t('card.check'))}</button></div>` +
          `<div class="temporal-slot" data-temporal="${x.id}"></div>`;
      }
      html += det('adv:' + x.id, t('card.advanced'), advancedHtml(x)) + '</div>';
      return html;
    })
    .join('');
  refreshTemporalSlots();
}

function mappingLabel(m: FrameMapping): string {
  if (m.mode === 'ratio') return t('mapping.ratio', { n: m.ratio });
  return t('mapping.' + m.mode);
}

function decodePath(app: App, id: SourceId): string {
  const c = app.sources.client(id);
  if (!c) return '—';
  for (const f of [app.sources.frameAt(id, app.player.tick)?.frame, 0]) {
    if (f === null || f === undefined) continue;
    const fr = c.getFrame(f);
    if (fr) return fr.format === 'RGBA' && !fr.sourceFormat?.startsWith('PNG') ? t('info.decodeBrowser') : `${fr.sourceFormat ?? fr.format} (${t('info.decodeRaw')})`;
  }
  return '—';
}

export async function exportTeamPreset(app: App): Promise<void> {
  const file = makePresetFile(app.project.name || 'team', app.customPresets, app.state.settings.shortcuts);
  const { downloadBlob } = await import('./dom');
  downloadBlob(new Blob([JSON.stringify(file, null, 2)], { type: 'application/json' }), 'aicomp-team.acpreset');
  toast(t('toast.presetExported'));
}
